// Convert CV markdown -> printable HTML opened in a new tab via Blob URL.
// Pop-up blocker safe when triggered from a user click handler.

import { getTemplate, printCss, DEFAULT_TEMPLATE_ID } from "./cvTemplates";

const escapeHtml = (s) =>
  s.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));

export const renderCvMarkdownToHtml = (markdown) => {
  const escaped = escapeHtml(markdown || "");
  const html = escaped
    .replace(/^---\s*$/gm, "<hr/>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^[\*\-] (.+)$/gm, "<li>$1</li>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
    .replace(/\n\n/g, "</p><p>");
  return `<p>${html}</p>`;
};

const buildPrintableDoc = (markdown, title, templateId) => {
  const template = getTemplate(templateId);
  const bodyHtml = renderCvMarkdownToHtml(markdown);
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${printCss(template)}</style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">Download PDF / Print</button>
    <button class="secondary" onclick="window.close()">Close</button>
    <span style="margin-left:auto; font-size:12px; opacity:.7;">Template: ${template.name} · Tip: choose "Save as PDF" in print dialog</span>
  </div>
  <div class="sheet">${bodyHtml}</div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { try { window.print(); } catch (e) {} }, 350);
    });
  </script>
</body>
</html>`;
};

/**
 * Open the CV in a new tab as a Blob URL and auto-trigger print. Falls back
 * to an in-page print() if pop-ups are blocked.
 */
export const openCvForPrint = (markdown, title = "CV", templateId = DEFAULT_TEMPLATE_ID) => {
  if (!markdown) return false;
  const doc = buildPrintableDoc(markdown, title, templateId);
  const blob = new Blob([doc], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    URL.revokeObjectURL(url);
    return inPagePrintFallback(markdown, templateId);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
};

const inPagePrintFallback = (markdown, templateId) => {
  const template = getTemplate(templateId);
  const v = template.vars;
  const styleId = "lumioos-cv-print-style";
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    @media print {
      body * { visibility: hidden !important; }
      #lumioos-cv-print, #lumioos-cv-print * { visibility: visible !important; }
      #lumioos-cv-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; font-family: ${v.fontFamily}; color: ${v.heading}; line-height: 1.55; font-size: 11pt; background: #fff; }
      #lumioos-cv-print h1 { font-size: 26pt; margin: 0 0 4px; letter-spacing: -0.02em; font-weight: ${v.h1Weight}; }
      #lumioos-cv-print h2 { font-size: 13pt; margin: 22px 0 8px; padding-bottom: 4px; border-bottom: ${v.ruleStyle} ${v.ruleColor}; text-transform: ${v.h2Transform}; letter-spacing: ${v.h2Spacing}; color: ${v.accent}; }
      #lumioos-cv-print h3 { font-size: 11pt; margin: 14px 0 4px; }
      #lumioos-cv-print em { color: ${v.muted}; font-style: italic; }
      #lumioos-cv-print ul, #lumioos-cv-print li { margin: 4px 0; padding-left: 18px; }
      #lumioos-cv-print p { margin: 6px 0; }
      @page { margin: 18mm 16mm; size: A4; }
    }
    #lumioos-cv-print { display: none; }
  `;
  let printDiv = document.getElementById("lumioos-cv-print");
  if (!printDiv) {
    printDiv = document.createElement("div");
    printDiv.id = "lumioos-cv-print";
    document.body.appendChild(printDiv);
  }
  printDiv.innerHTML = renderCvMarkdownToHtml(markdown);
  setTimeout(() => { try { window.print(); } catch (e) {} }, 50);
  return true;
};
